import '../../styles/fonts.css';
import React, { useState } from 'react';
import { useChatsContext } from '../../context/ChatsContext';
import { useSocket } from '../../hooks/useSocket';
import { chatAPI, groupChatAPI } from '../../services/api';
import { ExitIcon, QuestionIcon, RandomIcon, SearchIcon, GroupIcon } from '../../assets/SvgIcons';
import { InputModal } from '../ui/Modal';
import { GroupInfoModal } from '../ui/GroupInfoModal';

interface SearchButtonsProps {
  isDesktop?: boolean;
  onRandomSearch?: () => void;
  onSearchByNumber?: (number: string) => void;
}
interface CreateGroupResponse {
  groupChat: {
    id: number;
    name: string | null;
    description: string | null;
    creatorId: number;
    inviteCode: string;
    groupCode: string;
    createdAt: string;
    isActive: boolean;
    maxMembers: number;
  };
  qrCodeData: string;
}

interface NewGroupData {
  chatId: number;
  groupCode: string;
  inviteCode: string;
  qrCodeData: string;
}

export const SearchButtons: React.FC<SearchButtonsProps> = ({
  isDesktop = false,
  onRandomSearch,
  onSearchByNumber,
}) => {
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [groupName, setGroupName] = useState('');
  const [inviteCode, setInviteCode] = useState<string | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [isRandomSearching, setIsRandomSearching] = useState(false);
  const socket = useSocket();
  const { loadChats } = useChatsContext();
  const { createGroupChat } = useChatsContext();
  const [showGroupInfoModal, setShowGroupInfoModal] = useState(false);
  const [newGroup, setNewGroup] = useState<{ id: string; number: string } | null>(null);
  const [newGroupData, setNewGroupData] = useState<{
    chatId: number;
    groupCode: string;
    inviteCode: string;
    qrCodeData: string;
  } | null>(null);

  const handleRandomSearch = async () => {
    if (onRandomSearch) {
      onRandomSearch();
    } else {
      try {
        setIsRandomSearching(true);
        const response = await chatAPI.findRandomUser();
        socket.createChatByNumber(response.data.userNumber);
      } catch (error: any) {
        alert(error.response?.data?.message || 'Ошибка поиска случайного пользователя');
      } finally {
        setIsRandomSearching(false);
      }
    }
  };

  const handleCreateGroupClick = async () => {
    try {
      const response = await groupChatAPI.createGroup('', 100);
      const groupData = response.data as unknown as CreateGroupResponse;

      if (!groupData) throw new Error('Ответ сервера некорректный');

      setNewGroupData({
        chatId: groupData.groupChat.id,
        groupCode: groupData.groupChat.groupCode,
        inviteCode: groupData.groupChat.inviteCode,
        qrCodeData: groupData.qrCodeData,
      });

      setShowGroupInfoModal(true);
    } catch (error: any) {
      console.error('Ошибка создания группы:', error);
      alert(error.response?.data?.message || 'Ошибка создания группы');
    }
  };

  const handleSearchByNumber = async (numberToFind: string) => {
    if (onSearchByNumber) {
      onSearchByNumber(numberToFind);
    } else {
      if (!numberToFind.trim()) return;
      try {
        setIsSearching(true);
        socket.createChatByNumber(numberToFind.trim());
        setShowSearchModal(false);
      } catch (error: any) {
        alert(error.response?.data?.message || 'Ошибка поиска пользователя');
      } finally {
        setIsSearching(false);
      }
    }
  };

  if (!isDesktop) {
    return (
      <div
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: '#fff',
          padding: '12px',
          display: 'flex',
          justifyContent: 'center',
          gap: '16px',
        }}
      >
        <button
          onClick={handleRandomSearch}
          disabled={isRandomSearching}
          style={{
            background: 'none',
            border: '0px solid #000',
            padding: '8px',
            cursor: isRandomSearching ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '86px',
            height: '86px',
            opacity: isRandomSearching ? 0.6 : 1,
          }}
        >
          <div
            style={{
              width: '64px',
              height: '64px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: 'Tiny5, monospace',
              fontSize: '20px',
            }}
          >
            <RandomIcon />
          </div>
        </button>

        <button
          onClick={() => setShowSearchModal(true)}
          style={{
            background: 'none',
            border: '0px solid #000',
            padding: '8px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '86px',
            height: '86px',
          }}
        >
          <div
            style={{
              width: '64px',
              height: '64px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: 'Tiny5, monospace',
              fontSize: '20px',
            }}
          >
            <SearchIcon />
          </div>
        </button>

        <button
          onClick={handleCreateGroupClick}
          style={{
            background: 'none',
            border: '0px solid #000',
            padding: '8px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '86px',
            height: '86px',
          }}
        >
          <div
            style={{
              width: '64px',
              height: '64px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: 'Tiny5, monospace',
              fontSize: '20px',
            }}
          >
            <GroupIcon />
          </div>
        </button>

        <GroupInfoModal
          isOpen={showGroupInfoModal}
          onClose={() => {
            setShowGroupInfoModal(false);
            setNewGroupData(null);
          }}
          onConfirm={async (enteredName?: string) => {
            if (!newGroupData) return;

            try {
              setShowGroupInfoModal(false);

              if (enteredName && enteredName.trim()) {
                await groupChatAPI.updateGroupName(newGroupData.chatId, enteredName.trim());
              }

              await loadChats();

              setNewGroupData(null);
            } catch (err: any) {
              alert(err.response?.data?.message || 'Ошибка при переходе в комнату');
            }
          }}
          groupNumber={newGroupData?.groupCode || ''}
          inviteCode={newGroupData?.inviteCode || ''}
          qrCodeData={newGroupData?.qrCodeData}
        />

        <InputModal
          isOpen={showSearchModal}
          onClose={() => setShowSearchModal(false)}
          onConfirm={handleSearchByNumber}
          title='ВВЕДИТЕ НОМЕР ПОЛЬЗОВАТЕЛЯ'
          placeholder='00000000'
          maxLength={8}
          inputType='text'
          confirmText='НАЙТИ'
          cancelText='ОТМЕНА'
        />
      </div>
    );
  }

  return null;
};
